<template>
  <div class="gobot-widget" v-if="isAuthenticated">
    <!-- Launcher Button -->
    <button
      class="gobot-launcher"
      @click="togglePopup"
      aria-label="Open feedback widget"
      :aria-expanded="isOpen"
    >
      <img src="/img/icons/gobot.png" alt="" class="gobot-icon" />
    </button>

    <!-- Popup Panel -->
    <div
      v-if="isOpen"
      class="gobot-popup"
      role="dialog"
      aria-labelledby="gobot-title"
      aria-modal="true"
      ref="popup"
    >
      <div class="gobot-header">
        <h3 id="gobot-title" class="gobot-title">Gobot</h3>
        <button
          class="gobot-close"
          @click="closePopup"
          aria-label="Close feedback widget"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="gobot-body">
        <!-- Success State -->
        <div v-if="submitted" class="gobot-success">
          <p>Thanks! I'll pass that along right away. 🤘</p>
        </div>

        <!-- Form State -->
        <div v-else>
          <p class="gobot-intro">
            Hi! I'm Gobot. I can pass along any feedback, bug reports, or
            feature requests to the Gobo team. What would you like me to tell
            them?
          </p>

          <div class="gobot-form">
            <textarea
              v-model="message"
              class="gobot-textarea"
              placeholder="Type your message here..."
              rows="4"
              :disabled="submitting"
              ref="textarea"
            ></textarea>

            <div v-if="errorMessage" class="gobot-error">
              {{ errorMessage }}
            </div>

            <button
              class="gobot-submit"
              @click="submitFeedback"
              :disabled="!message.trim() || submitting"
            >
              <span v-if="submitting">Sending...</span>
              <span v-else>Send to Gobo</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Click Outside Overlay -->
    <div v-if="isOpen" class="gobot-overlay" @click="closePopup"></div>
  </div>
</template>

<script>
import firebase from "firebase/app";
import "firebase/firestore";
import { mapGetters } from "vuex";

export default {
  name: "GobotWidget",
  data() {
    return {
      isOpen: false,
      message: "",
      submitting: false,
      submitted: false,
      errorMessage: ""
    };
  },
  computed: {
    ...mapGetters({
      user: "user",
      venues: "venues"
    }),
    isAuthenticated() {
      return this.user && this.user.loggedIn && this.user.uid;
    },
    currentVenue() {
      // Get the first venue if user is a venue manager
      if (this.venues && this.venues.length > 0) {
        return this.venues[0];
      }
      return null;
    }
  },
  methods: {
    togglePopup() {
      if (this.isOpen) {
        this.closePopup();
      } else {
        this.openPopup();
      }
    },
    openPopup() {
      this.isOpen = true;
      this.errorMessage = "";
      // Focus the textarea when popup opens
      this.$nextTick(() => {
        if (this.$refs.textarea) {
          this.$refs.textarea.focus();
        }
        // Set up focus trap
        this.setupFocusTrap();
      });
    },
    closePopup() {
      this.isOpen = false;
      // Reset form state after closing (with delay for animation)
      setTimeout(() => {
        if (!this.isOpen) {
          this.message = "";
          this.submitted = false;
          this.errorMessage = "";
        }
      }, 300);
    },
    setupFocusTrap() {
      // Simple focus trap for accessibility
      const popup = this.$refs.popup;
      if (!popup) return;

      const focusableElements = popup.querySelectorAll(
        'button, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      popup.addEventListener("keydown", e => {
        if (e.key === "Escape") {
          this.closePopup();
          return;
        }

        if (e.key !== "Tab") return;

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      });
    },
    async submitFeedback() {
      if (!this.message.trim() || this.submitting) return;

      this.submitting = true;
      this.errorMessage = "";

      try {
        var db = firebase.firestore();

        const feedbackData = {
          message: this.message.trim(),
          userId: this.user.uid,
          userEmail: this.user.email || "",
          venueId: this.currentVenue ? this.currentVenue.id : "",
          venueName: this.currentVenue ? this.currentVenue.name : "",
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
          status: "new"
        };

        await db.collection("userFeedback").add(feedbackData);

        console.log("User feedback submitted successfully");
        this.submitted = true;
        this.message = "";
      } catch (error) {
        console.error("Error submitting feedback:", error);
        this.errorMessage =
          "Sorry, there was a problem sending your feedback. Please try again.";
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.gobot-widget {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
}

.gobot-launcher {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: #ffffff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease, box-shadow 0.2s ease,
    background-color 0.2s ease;
  position: relative;
  z-index: 10001;

  &:hover {
    background-color: $active-green;
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
  }

  &:focus {
    outline: 2px solid $active-green;
    outline-offset: 2px;
  }
}

.gobot-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.gobot-popup {
  position: absolute;
  bottom: 70px;
  right: 0;
  width: 320px;
  background-color: #ffffff;
  border-radius: $radius;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  z-index: 10001;
}

.gobot-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background-color: $active-green;
  color: #ffffff;
}

.gobot-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  font-family: $family-sans-serif;
}

.gobot-close {
  background: none;
  border: none;
  color: #ffffff;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }

  &:focus {
    outline: 2px solid #ffffff;
    outline-offset: 2px;
  }
}

.gobot-body {
  padding: 16px;
}

.gobot-intro {
  margin: 0 0 16px 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: $gray2;
}

.gobot-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.gobot-textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid $gray5;
  border-radius: $radius;
  font-family: $family-sans-serif;
  font-size: 0.875rem;
  resize: vertical;
  min-height: 100px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: $active-green;
  }

  &:disabled {
    background-color: $gray6;
    cursor: not-allowed;
  }

  &::placeholder {
    color: $gray4;
  }
}

.gobot-error {
  padding: 8px 12px;
  background-color: $muted-red;
  color: $oops-red;
  border-radius: $radius;
  font-size: 0.8rem;
}

.gobot-submit {
  padding: 10px 16px;
  background-color: $active-green;
  color: #ffffff;
  border: none;
  border-radius: $radius;
  font-family: $family-sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease, opacity 0.2s ease;

  &:hover:not(:disabled) {
    background-color: darken($active-green, 5%);
  }

  &:focus {
    outline: 2px solid $active-green;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.gobot-success {
  text-align: center;
  padding: 24px 0;

  p {
    margin: 0;
    font-size: 1rem;
    color: $gray1;
  }
}

.gobot-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;
}
</style>
